import { Form } from '@ant-design/compatible';
import React, { useEffect, useState } from 'react';
import Button from '../../../../components/Button';
import CountOfTotal from '../../../../components/CountOfTotal';
import BaseForm from '../../../../components/form/BaseForm';
import ImgUploadForm from '../../../../components/form/ImgUploadForm';
import InputForm from '../../../../components/form/InputForm';
import TextAreaForm from '../../../../components/form/TextAreaForm';
import ImgUpload from '../../../../components/ImgUpload/ImgUpload';
import Anchor from '../../../../components/link/Anchor';
import Panel from '../../../../components/outline/Panel';
import {
  HOMEMENU_IMG_HEIGHT,
  HOMEMENU_IMG_WIDTH,
  MAXIMUM_NUMBER_OF_HOME_MENU_MEMO_TEXT,
  MAXIMUM_NUMBER_OF_HOME_MENU_TEXT,
  WIDGET_HOME_MENU_LINK_TYPE_SELECT_LIST,
} from '../../../../constants/service/app/widgetConstants';
import { APP_LINK_TYPE_URL, IMAGE_SIZE_GUIDE_TEXT } from '../../../../constants/service/common/commonConstants';
import { asyncSimpleModal } from '../../../../lib/common';
import { formRequiredRules } from '../../../../lib/formRules';
import GlobalLinkForm from '../../../../components/form/GlobalLinkForm/GlobalLinkForm';
import { REQUIRED_MSG } from '../../../../constants/service/common/formRulesConstants';
import styles from './WidgetForm.module.scss';

const WidgetForm = ({ menuItem, onSubmit, onCancel, form }) => {
  const [editMode, setEditMode] = useState(false);

  /**
   * 다른 메뉴를 선택하면 선택된 메뉴의 값으로 업데이트
   */
  useEffect(() => {
    setEditMode(false);
  }, [menuItem.id]);

  useEffect(() => {
    if (menuItem.isUpdated) {
      setEditMode(false);
    }
  }, [menuItem.isUpdated]);

  const onEditClick = () => {
    setEditMode(true);
  };

  const onCancelClick = async (e) => {
    e.preventDefault();

    if (form.isFieldsTouched()) {
      const confirmResult = await asyncSimpleModal({
        type: 'confirm',
        title: '메뉴 편집',
        message: (
          <>
            해당 메뉴 편집을 중단하시겠습니까?
            <br />
            확인 버튼을 누를 시, 현재 편집하던 내용은 사라지게 됩니다.
          </>
        ),
      });

      if (!confirmResult) return;

      setEditMode(false);
      onCancel();
    } else {
      setEditMode(false);
    }
  };

  const onSubmitClick = (e) => {
    e.preventDefault();

    form.validateFields((err, values) => {
      if (!err) {
        const { link, ...rest } = values;

        onSubmit({
          ...link,
          ...rest,
          id: menuItem.id,
        });
      }
    });
  };

  const urlForLink = ({ linkType, linkId, linkUrl }) => {
    let returnUrl = linkUrl;

    if (Object.prototype.hasOwnProperty.call(APP_LINK_TYPE_URL, linkType)) {
      returnUrl = APP_LINK_TYPE_URL[linkType].replace(':id', linkId);
    }

    return returnUrl;
  };

  return (
    <Panel className={styles.homeMenuForm}>
      <Form onSubmit={onSubmitClick}>
        <div className={`panel-header ${styles.panelHeader}`}>
          <div className="title">메뉴 등록 및 편집</div>

          <div className={styles.actions}>
            {!editMode ? (
              <>
                <Button isRadius color="primary" text="편집" type="button" onClick={onEditClick} />
              </>
            ) : (
              <>
                <Button type="reset" isRadius color="light" text="취소" onClick={onCancelClick} />

                <Button isRadius color="primary" text="저장" type="submit" onClick={onSubmitClick} />
              </>
            )}
          </div>
        </div>

        <div className={`panel-body ${styles.panelBody}`}>
          {!editMode ? (
            <>
              <BaseForm label="메뉴 순서" content={menuItem.id} />

              <BaseForm label="메뉴 제목" content={menuItem.title} />

              <BaseForm
                label="메뉴 이미지"
                content={
                  <ImgUpload
                    list={menuItem.url ? [{ url: menuItem.url, path: menuItem.path }] : []}
                    minCnt={1}
                    modalTitle="메뉴 이미지"
                    isEditable={false}
                    cropMode={false}
                  />
                }
              />

              <BaseForm label="메모" content={menuItem.memo} />

              <BaseForm
                label="링크 종류"
                content={WIDGET_HOME_MENU_LINK_TYPE_SELECT_LIST.find((item) => item.id === menuItem.linkType)?.name}
              />

              <BaseForm
                label="연결될 링크"
                content={
                  <Anchor
                    isUnderLine
                    color="primary"
                    href={urlForLink({
                      linkType: menuItem.linkType,
                      linkId: menuItem.linkId,
                      linkUrl: menuItem.linkUrl,
                    })}
                    text={menuItem.linkUrl ? menuItem.linkUrl : `${menuItem.linkName}(${menuItem.linkId})`}
                    target="_blank"
                  />
                }
              />
            </>
          ) : (
            <>
              <BaseForm label="메뉴 순서" content={menuItem.id} />

              <InputForm
                label={
                  <>
                    <div>메뉴 제목</div>
                    <CountOfTotal
                      content={form.getFieldValue('title') || menuItem.title}
                      maxCnt={MAXIMUM_NUMBER_OF_HOME_MENU_TEXT}
                    />
                  </>
                }
                form={form}
                isEditable={true}
                isTrim={false}
                rules={[formRequiredRules(true, REQUIRED_MSG)]}
                initialValue={menuItem.title}
                maxLength={MAXIMUM_NUMBER_OF_HOME_MENU_TEXT}
                name="title"
                subContent={<div className={styles.subInfo}>* 메뉴 제목은 최대 8글자입니다</div>}
              />

              <ImgUploadForm
                form={form}
                label="메뉴 이미지"
                isEditable={true}
                enableGif={true}
                isProductAttachment={false}
                initialValue={menuItem.url ? [{ url: menuItem.url, path: menuItem.path }] : []}
                rules={[formRequiredRules(true, REQUIRED_MSG, true, 'array')]}
                minCnt={1}
                modalTitle="메뉴 이미지"
                cropMode={false}
                capacityLimit={1}
                name="imgList"
                targetSize={{
                  width: HOMEMENU_IMG_WIDTH,
                  height: HOMEMENU_IMG_HEIGHT,
                }}
                restriction={{
                  square: true,
                }}
                subContent={
                  <div className={styles.imgSubInfo}>
                    * 각 메뉴 별 App 홈에 노출될 이미지입니다.
                    <br />
                    <br />
                    * 메뉴 이미지 : 정방형 (작업 사이즈 72*72)
                    <br />
                    * 권장 크기: 216 x 216 px
                    <br />
                    * png, jpg, jpeg, gif 형식의 이미지만 등록 가능합니다.
                    <br />* {IMAGE_SIZE_GUIDE_TEXT}
                  </div>
                }
              />

              {/*메모*/}
              <TextAreaForm
                form={form}
                label={
                  <>
                    <div>메모</div>
                    <CountOfTotal
                      content={form.getFieldValue('memo') || menuItem.memo}
                      maxCnt={MAXIMUM_NUMBER_OF_HOME_MENU_MEMO_TEXT}
                    />
                  </>
                }
                rules={[formRequiredRules(true, '메모를 입력해주세요.')]}
                initialValue={menuItem.memo}
                name="memo"
                maxLength={MAXIMUM_NUMBER_OF_HOME_MENU_MEMO_TEXT}
                rows={5}
              />

              <GlobalLinkForm
                label={'연결될 링크'}
                form={form}
                list={WIDGET_HOME_MENU_LINK_TYPE_SELECT_LIST}
                initialValue={{
                  linkType: menuItem.linkType,
                  linkId: menuItem.linkId,
                  linkUrl: menuItem.linkUrl,
                  linkName: menuItem.linkName,
                }}
                rules={[{ required: true }]}
                name="link"
              />
            </>
          )}
        </div>
      </Form>
    </Panel>
  );
};

export default Form.create({
  onFieldsChange(props, changedFields) {
    props.onChangeMenuItem(changedFields);
  },
})(WidgetForm);
